import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components/image/src/image";
import { Link } from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

import type { BgColors } from "../../link/src/link";

export type CustomerStatColor =
    | "blue"
    | "blueLight"
    | "lime"
    | "limeLight"
    | "purple"
    | "purpleLight"
    | "orange"
    | "orangeLight"
    | "black";

export type CustomerStatProps = {
    logoSrc: string;
    logoAlt: string;
    statValue: string;
    statModifier?: string;
    description: string;
    linkText?: string;
    linkHref?: string;
    linkDataEventProps?: DataEventProps;
    color?: CustomerStatColor;
    motionConfig?: {
        transformForward: string;
        transformBackward: string;
    };
};

const colorMap: Record<
    CustomerStatColor,
    { bg: string; text: string; linkBg: BgColors }
> = {
    blue: {
        bg: wacToken("color.extended.blue700"),
        text: wacToken("color.neutral.light0"),
        linkBg: "bgBlackBlue"
    },
    blueLight: {
        bg: wacToken("color.extended.blue200"),
        text: wacToken("color.primary.black"),
        linkBg: "bgColored"
    },
    lime: {
        bg: wacToken("color.extended.lime800"),
        text: wacToken("color.neutral.light0"),
        linkBg: "bgBlackBlue"
    },
    limeLight: {
        bg: wacToken("color.extended.lime200"),
        text: wacToken("color.primary.black"),
        linkBg: "bgColored"
    },
    purple: {
        bg: wacToken("color.extended.purple800"),
        text: wacToken("color.neutral.light0"),
        linkBg: "bgBlackBlue"
    },
    purpleLight: {
        bg: wacToken("color.extended.purple200"),
        text: wacToken("color.primary.black"),
        linkBg: "bgColored"
    },
    orange: {
        bg: wacToken("color.extended.orange700"),
        text: wacToken("color.neutral.light0"),
        linkBg: "bgBlackBlue"
    },
    orangeLight: {
        bg: wacToken("color.extended.saffron200"),
        text: wacToken("color.primary.black"),
        linkBg: "bgColored"
    },
    black: {
        bg: wacToken("color.primary.black"),
        text: wacToken("color.neutral.light0"),
        linkBg: "bgBlackBlue"
    }
};

const containerCss = css({
    "display": "flex",
    "flexDirection": "column",
    "gap": token("space.500"),
    "borderRadius": wacToken("corner.radius.card"),
    "padding": token("space.400"),
    "width": "280px",
    [media.only.sm]: {
        width: "320px"
    },
    [media.only.md]: {
        width: "360px"
    },
    [media.above.lg]: {
        width: "380px"
    },

    // animation hook when in a gallery container with extra motion enabled
    "willChange": "transform",
    "transform": `translateX(calc(var(--gc-scroll-speed) * var(--gc-item-i, 0) / 9 * 20rem))`,
    ["@container style(--gc-scroll-direction <= 0)"]: {
        transform: `translateX(calc(var(--gc-scroll-speed) * (9 - var(--gc-item-i, 0)) / 9 * -20rem))`
    },

    // styles when card contains a link
    "position": "relative",
    // Need to style anchors differently for a linkable card and cannot pass in CompiledCSS
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".full-card"]: {
        "::before": {
            content: "",
            inset: "0",
            position: "absolute",
            zIndex: 1
        },
        "&:link, &:visited, &:hover, &:active": {
            "color": "inherit",
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "& span, & > svg": {
                opacity: 1,
                mixBlendMode: "normal"
            }
        }
    },
    // motion on hover
    "&::before": {
        content: "",
        position: "absolute",
        zIndex: -1,
        insetBlockStart: 0,
        insetInline: 0,
        borderStartEndRadius: wacToken("corner.radius.card"),
        borderStartStartRadius: wacToken("corner.radius.card"),
        height: "48px",
        backgroundColor: "inherit",
        transition: "transform 250ms ease-in"
    },
    ["&:has(.full-card:hover), &:has(.full-card:focus)"]: {
        "&::before": {
            transition: "transform 250ms ease-out",
            transform: "translateY(-24px)"
        }
    }
});

const logoImageCss = css({
    objectFit: "contain",
    objectPosition: "left center",
    width: "55%",
    height: "24px",
    [media.above.md]: {
        height: "32px"
    }
});

/** Dark stat surfaces use light body text; invert a dark logo asset so it reads as light (same idea as `CustomerQuote`). */
const logoOnDarkSurfaceCss = css({
    filter: "brightness(0) saturate(100%) invert(100%)",
    ["-webkit-filter"]: "brightness(0) saturate(100%) invert(100%)"
});

const fullStatCss = css({
    display: "grid",
    gap: "16px"
});

const statRowCss = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "baseline",
    gap: token("space.100")
});

/** Atlassian Mono — matches Desktop XL spec at `above.lg`; scales down on smaller breakpoints. */
const statValueCss = css({
    fontFamily: "'Atlassian Mono', monospace",
    fontStyle: "normal",
    fontWeight: 700,
    letterSpacing: "-3px",
    margin: 0,
    fontFeatureSettings: '"liga" off, "clig" off',
    fontSize: "60px",
    lineHeight: "80px",
    [media.only.sm]: {
        fontSize: "70px",
        lineHeight: "80px"
    },
    [media.only.md]: {
        fontSize: "80px",
        lineHeight: "80px"
    },
    [media.above.lg]: {
        fontSize: "96px",
        lineHeight: "96px"
    },
    textBoxEdge: "cap alphabetic",
    textBoxTrim: "trim-end"
});

const statModifierCss = css({
    fontFamily: "'Atlassian Mono', monospace",
    fontStyle: "normal",
    fontWeight: 700,
    letterSpacing: "-3px",
    margin: 0,
    fontFeatureSettings: '"liga" off, "clig" off',
    fontSize: "32px",
    lineHeight: "60px",
    [media.only.sm]: {
        fontSize: "40px",
        lineHeight: "60px"
    },
    [media.only.md]: {
        fontSize: "40px",
        lineHeight: "60px"
    },
    [media.above.lg]: {
        fontSize: "64px",
        lineHeight: "76px"
    },
    textBoxEdge: "cap alphabetic",
    textBoxTrim: "trim-end"
});

const linkWrapCss = css({
    fontSize: "1rem",
    [media.above.md]: {
        fontSize: "1.125rem"
    }
});

export const CustomerStat = ({
    logoSrc,
    logoAlt,
    statValue,
    statModifier,
    description,
    linkText,
    linkHref,
    linkDataEventProps,
    color = "blue",
    motionConfig
}: CustomerStatProps) => {
    const { bg: surfaceBg, text: surfaceText, linkBg } = colorMap[color];
    const logoOnDarkSurface = surfaceText === wacToken("color.neutral.light0");

    return (
        <section
            css={[
                containerCss,
                motionConfig?.transformForward &&
                    css({
                        transform: motionConfig?.transformForward,
                        ["@container style(--gc-scroll-direction <= 0)"]: {
                            transform: motionConfig?.transformBackward
                        }
                    })
            ]}
            style={{
                backgroundColor: surfaceBg,
                color: surfaceText
            }}
            data-testid="customer-stat"
        >
            <Image
                css={[logoImageCss, logoOnDarkSurface && logoOnDarkSurfaceCss]}
                src={logoSrc}
                alt={logoAlt}
                height="40"
            />
            <div css={fullStatCss}>
                <div css={statRowCss}>
                    <span css={statValueCss}>{statValue}</span>
                    {statModifier && (
                        <span css={statModifierCss}>{statModifier}</span>
                    )}
                </div>

                <p
                    className="callout-sm"
                    css={css({
                        margin: 0,
                        textBoxEdge: "cap alphabetic",
                        textBoxTrim: "trim-end"
                    })}
                    style={{ color: surfaceText }}
                >
                    {description}
                </p>
            </div>
            {linkText && linkHref && (
                <div css={linkWrapCss}>
                    <Link
                        text={linkText}
                        href={linkHref}
                        type="primary"
                        bgColor={linkBg}
                        ariaLabel={linkText}
                        aria-label={linkText}
                        className="full-card"
                        dataEventProps={linkDataEventProps}
                    />
                </div>
            )}
        </section>
    );
};
